/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { jsx, css } from '@emotion/react';
import { queryCache, useQuery } from 'react-query';
import Page from '@atlaskit/page';
import api from '../../libs/api';
import Skeleton from 'react-loading-skeleton';
import { PageWrapper } from '../../components/page';
import PageHeader from '@atlaskit/page-header';
import { FormattedMessage, FormattedNumber, useIntl } from 'components/i18n';
import { useNotifications } from '../../components/notification';
import { useConfirm } from '../../components/modal-dialog';
import useSearchable from '../../hooks/useSearchable';
import useAccountModal from './useAccountModal';
import Table, { Cell, VisibleOnRowHover, Column } from 'components/table';
import TextField from '../../components/textfield';
import Button from '@atlaskit/button';
import { MoreDropdownCell } from '../sale/SaleItemsPage';

type Props = {}

export type Account = {
  accountNumber: number;
  description: string;
  type: string;
  active: boolean;
  vof?: boolean;
  eenmanszaak?: boolean;
  bv?: boolean;
  tags?: null | string[];
  isNew?: boolean;
  subCategoryNumber?: number;
}

const fetchAccounts = async (key: string) => {
  return api.get('/accounts');
};
const deleteAccount = async (accountNumber: number) =>
  api.delete(`/accounts/${accountNumber}`);

const GeneralLedgerPage: React.FC<Props> = (props) => {


  const intl = useIntl();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const { confirmDelete } = useConfirm();

  const { data, error, isLoading } = useQuery(
    ['accounts'],
    fetchAccounts
  );

  const [search, setSearch] = useState('');
  const accounts: Account[] = useSearchable(data, search, {
    keys: ['name', 'description', 'accountNumber', 'tags']
  }) || [];

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),[]);

  const showAccountModal = useAccountModal();

  const handleNewItem = useCallback(() => {
    showAccountModal({
      account: {accountNumber: 0, description: '', type:'', active: true,  isNew: true},
      isNew: true,
      onSubmit: async (account: Account) => {
        showSuccessNotification(
          <FormattedMessage
            id="admin.GeneralLedgerPage.notifications.created.success.title"
            defaultMessage="Account created"
          />)
      },
    });
  },[]);
  const handleEdit = useCallback((item) => {
    showAccountModal({
      account: item,
      onSubmit: async (account: Account) => {
        showSuccessNotification(
          <FormattedMessage
            id="admin.GeneralLedgerPage.notifications.updated.success.title"
            defaultMessage="Account updated"
          />)
      },
    });
  },[]);

  const handleDelete = useCallback(async (account: Account) => {
    confirmDelete({
      title: (
        <FormattedMessage
          id="admin.GeneralLedgerPage.modals.delete.title`"
          defaultMessage="Delete this account?"
        />
      ),
      content: (
        <FormattedMessage
          id="admin.GeneralLedgerPage.modals.delete.content`"
          defaultMessage={`You are about to delete account "{name}".`}
          values={{ name: account.description }}
        />
      ),
      onConfirm: async () => {
        try {
          await deleteAccount(account.accountNumber);
          showSuccessNotification(
            <FormattedMessage
              id="admin.GeneralLedgerPage.notifications.delete.success.title`"
              defaultMessage="Account deleted"
            />
          );
        } catch (e) {
          showErrorNotification(e.message);
        }
        queryCache.invalidateQueries('accounts');
      },
    });
  }, []);

  const columns: Column<Account>[] = useMemo(() => [
    {
      Header: <FormattedMessage id="admin.GeneralLedgerPage.headings.name" defaultMessage="Name"/>,
      accessor: 'description',
      width: 240,
      Cell: ({row: {original: account}}) => <Cell primaryText={account.accountNumber} secondaryText={account.description}/>
    },
    {
      Header: <FormattedMessage id="admin.GeneralLedgerPage.headings.type" defaultMessage="Type"/>,
      accessor: 'type',
      width: 60,
      Cell: ({value}) => <Cell primaryText={""} secondaryText={value}/>
    },
    {
      Header: <FormattedMessage id="admin.GeneralLedgerPage.headings.tags" defaultMessage="Tags"/>,
      accessor: 'tags',
      width: 100,
      Cell: ({value}) => <Cell primaryText="" secondaryText={value? value.join(', ') : ''}/>
    },
    {
      Header: <FormattedMessage id="admin.GeneralLedgerPage.headings.inactive" defaultMessage="Inactive"/>,
      accessor: 'active',
      width: 50,
      align: 'right',
      Cell: ({value}) => value? '' : 'X',
    },
    {
      Header: <FormattedMessage id="admin.GeneralLedgerPage.headings.eenmanszaak" defaultMessage="Eenmanszaak"/>,
      accessor: 'eenmanszaak',
      width: 100,
      align: 'right',
      Cell: ({row: {original: account}}) =>  {
        const active = [];
        if(account.eenmanszaak) active.push('zzp');
        if(account.vof) active.push('vof');
        if(account.bv) active.push('bv');
        return active.join(', ')
      }
    },
    {
      id: 'actions',
      Header: "",
      minWidth: 56,
      width: 56,
      align: 'right',
      accessor: 'accountNumber',
      Cell: ({row: {original: account}}) => <MoreDropdownCell onEdit={handleEdit} onDelete={handleDelete} item={account}/>
    },
  ],[]);

  const actionsContent = React.useMemo(() => {
      const searchLabel = intl.formatMessage({
        id: 'admin.GeneralLedgerPage.search',
        defaultMessage: 'Search',
        description: 'Search placeholder',
      });

      const itemsContainer = css`
      display: flex;
      align-items: center;
      justify-content: flex-end;
    `;

      return (
        <div css={itemsContainer}>
          <div css={css`flex: 0 0 300px; margin-right: 1rem`}>
            <TextField placeholder={searchLabel} isCompact aria-label={searchLabel} // onChange={handleSearch}
                       onChange={handleSearch}
            />
          </div>
          <Button appearance="primary" onClick={() => handleNewItem()}>
            <FormattedMessage
              id="admin.GeneralLedgerPage.create"
              defaultMessage="Create account"
            />
          </Button>
        </div>
      )}
    ,[intl]);


  if(!data) {
    return <Skeleton/>
  }

  return (
  <PageWrapper>
    <PageHeader actions={actionsContent} ><FormattedMessage id="admin.GeneralLedgerPage.title" defaultMessage="General ledger management" />
    </PageHeader>
    <Table<Account> columns={columns} data={accounts} pageSize={25} hasPagination manualPagination={false}/>
  </PageWrapper>);


  return (
    <PageWrapper fullWidth>
      <Page>
        <PageHeader>
          <FormattedMessage
            id="admin.GeneralLedgerPage.title"
            defaultMessage="General ledger management"
          />
        </PageHeader>

        {/*<div css={css`margin-top: 4rem;`}></div>*/}
        <table >
          <thead>
          <tr>
            <th>Nummer</th>
            <th>Omschrijving</th>
            <th>Type</th>
            <th>Niet actief</th>
            <th>Eenmanszaak</th>
            <th>VOF</th>
            <th>BV</th>
            <th>Tags</th>
          </tr>
          </thead>
          <tbody>
    {accounts.map(account => {
      return (
        <tr key={account.accountNumber}>
          <td>{account.accountNumber}</td>
          <td>{account.description}</td>
          <td>{account.type}</td>
          <td>{account.active? "" : 'X'}</td>
          <td>{account.eenmanszaak? "X" : ''}</td>
          <td>{account.vof? "X" : ''}</td>
          <td>{account.bv? "X" : ''}</td>
          <td>{account.tags && account.tags.join(', ')}</td>
      </tr>)
    })}
          </tbody>

  </table>
      </Page>
    </PageWrapper>);
};

export default GeneralLedgerPage;
